/**
 * Centralized input formatting utilities
 * Used across the application for consistent phone number, zip code, and other input formatting
 */

/**
 * Format phone number to (XXX) XXX-XXXX format
 * @param {string} value - Raw phone number input
 * @returns {string} Formatted phone number
 */
export const formatPhoneNumber = (value) => {
  if (!value) return '';
  
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Apply formatting based on number of digits
  if (digits.length >= 6) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  } else if (digits.length >= 3) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  } else {
    return digits;
  }
};

/**
 * Validate phone number format
 * @param {string} value - Phone number to validate
 * @returns {string|null} Error message or null if valid
 */
export const validatePhoneNumber = (value) => {
  if (!value) return null;
  
  const digits = value.replace(/\D/g, '');
  
  if (digits.length < 10) {
    return 'Please enter a valid 10-digit phone number';
  }
  
  if (digits.length > 10) {
    return 'Phone number should be 10 digits';
  }
  
  return null;
};

/**
 * Format zip code to ensure only digits
 * @param {string} value - Raw zip code input
 * @returns {string} Formatted zip code (digits only, max 5 chars)
 */
export const formatZipCode = (value) => {
  if (!value) return '';
  
  // Keep only digits and limit to 5 characters
  const digits = value.replace(/\D/g, '').slice(0, 5);
  return digits;
};

/**
 * Validate zip code format
 * @param {string} value - Zip code to validate
 * @returns {string|null} Error message or null if valid
 */
export const validateZipCode = (value) => {
  if (!value) return null;
  
  const zipRegex = /^\d{5}$/;
  
  if (!zipRegex.test(value)) {
    return 'Please enter a valid 5-digit ZIP code';
  }
  
  return null;
};

/**
 * Get unformatted phone number (digits only)
 * @param {string} value - Formatted phone number
 * @returns {string} Digits only
 */
export const getPhoneDigits = (value) => {
  return value ? value.replace(/\D/g, '') : '';
};

/**
 * Check if phone number is complete (10 digits)
 * @param {string} value - Phone number to check
 * @returns {boolean} True if complete
 */
export const isPhoneComplete = (value) => {
  return getPhoneDigits(value).length === 10;
};

/**
 * Check if zip code is complete (5 digits)
 * @param {string} value - Zip code to check
 * @returns {boolean} True if complete
 */
export const isZipComplete = (value) => {
  return /^\d{5}$/.test(value);
};